import {
    PdsButton,
    PdsFileUpload,
    PdsGrid,
    PdsGridItem,
    PdsHeading,
    PdsLayoutContainer,
    PdsModal,
    PdsSidebar,
    PdsTextInput
} from "@principal/design-system-react";
import { DateTime } from "luxon";
import { Claim } from "../domain/Claim";
import { Document } from "../domain/Document";
import ClaimStatusButton from "../atoms/ClaimStatusButton";
import { Relationship } from "../enums/Relationship";
import { findKeyByValue, snakeCaseToCapitalized } from "../enums/FindKeyByValue";
import Benefits from "../molecules/Benefits";
import { useParams } from "react-router-dom";
import { useClaims } from "../hooks/useClaims";
import LoadingSpinner from "../atoms/LoadingSpinner";
import React, { useRef, useState } from "react";
import {
    COMPLETE_SHAREPOINT_UPLOAD_MUTATION,
    CREATE_SHAREPOINT_UPLOAD_SESSION_MUTATION,
    FIND_CLAIM_ANALYST_QUERY,
    FIND_CLAIM_DOCUMENT_QUERY,
    FIND_CLAIMS_QUERY,
    FIND_EXPLANATION_OF_BENEFITS_QUERY
} from "../atoms/Queries";
import { PdsIconAlertTriangle, PdsIconCheckCircle, PdsIconLoader } from "@principal/design-system-icons-react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import pds from '../pds.module.scss';
import { ClaimStatus } from "../enums/ClaimStatus";
import DataTable from "../molecules/DataTable";
import { Payment } from "../domain/Payment";
import fileDownload from 'js-file-download'
import { Buffer } from "buffer";
import { DisabilityClaim } from "../domain/claims/disability/DisabilityClaim";
import FlatTable from "../molecules/FlatTable";
import { Coverage } from "../enums/Coverage";
import { useCoverage } from "../hooks/useCoverage";
import { useMember } from "../hooks/useMember";
import AdobeDocuSignLink from "../atoms/AdobeDocuSignLink";
import { Party } from "../enums/Party";
import PaymentPreferenceModal from "../molecules/PaymentPreferenceModal";
import { SourceSystem } from "../enums/claims/disability/SourceSystem";
import { FeatureType, User } from "../generated/graphql";
import { useFeature } from "../hooks/useFeature";
import axios from "axios";
import findPaperClaimFormNumberFor = Coverage.findPaperClaimFormNumberFor;
import findDepartmentExtensionFor = Coverage.findDepartmentExtensionFor;
import isInClaimsService = SourceSystem.isInClaimsService;
import axiosRetry from "axios-retry";

const ClaimDetail = () => {
    const { claimId } = useParams();
    const coverage = useCoverage();
    const { loading: memberLoading, data: member, error: memberError } = useMember()
    const { loading: claimsLoading, data: claims, error: claimsError, variables } = useClaims()
    const { loading: analystLoading, data: analystData, error: analystError } = useQuery(FIND_CLAIM_ANALYST_QUERY, { variables: { claimId } })
    const analyst = analystData?.findClaimAnalyst as User
    const { loading: hasUpdatePaymentPreferenceLoading, data: hasUpdatePaymentPreference } = useFeature(FeatureType.UpdatePaymentPreference)
    const [findExplanationOfBenefits] = useLazyQuery(FIND_EXPLANATION_OF_BENEFITS_QUERY)
    const [findClaimDocument] = useLazyQuery(FIND_CLAIM_DOCUMENT_QUERY)
    const [createSharepointUploadSession] = useMutation(CREATE_SHAREPOINT_UPLOAD_SESSION_MUTATION, {
        context: {
            headers: {
                'Apollo-Require-Preflight': true
            }
        }
    })
    const [completeSharepointUploadMutation, { data: uploadData, error: uploadError }] = useMutation(COMPLETE_SHAREPOINT_UPLOAD_MUTATION, {
        context: {
            headers: {
                'Apollo-Require-Preflight': true
            }
        }
    })

    const [paymentModalContents, setPaymentModalContents] = useState(<></>);
    const [file, setFile] = useState(null as any as File);
    const [fileDescription, setFileDescription] = useState('');
    const [uploadInProgress, setUploadInProgress] = useState(false);

    const paymentModalRef = useRef(null) as React.RefObject<any>;
    const fileUploadRef = useRef(null) as React.RefObject<any>;
    const paymentPreferenceRef = useRef(null) as React.RefObject<any>;

    const claim = claims?.find((c: Claim) => c.claimId === claimId)
    const notInClaimsService = !claim || !isInClaimsService(claim.sourceSystem);
    const submissionDate = claim?.submitted?.toLocaleString(DateTime.DATE_SHORT);
    const departmentExtension = findDepartmentExtensionFor(coverage)
    const phoneNumber = `800-245-1522${departmentExtension ? ` x${departmentExtension}` : ''} (Español: 800-243-1404)`;
    return (
        <>
            <div id="claim" className="pds-u-background-brand-gradient-subtle">
                <PdsLayoutContainer>
                    {buildClaim()}
                </PdsLayoutContainer>
            </div>
            <div id="recent-updates">
                <PdsLayoutContainer>
                    {buildRecentUpdates()}
                </PdsLayoutContainer>
            </div>
            <Benefits relationship={claim?.relationship}/>
            <div id="payments" className="pds-u-background-subtle">
                <PdsLayoutContainer>
                    {buildPayments()}
                    <PdsModal modalTitle="Payment benefits"
                              size="lg"
                              ref={paymentModalRef}>
                        {paymentModalContents}
                    </PdsModal>
                </PdsLayoutContainer>
            </div>
        </>
    );

    function buildClaim() {
        if(claimsLoading) {
            return <LoadingSpinner/>
        } else if(claimsError) {
            console.log('claims error', claimsError)
            return <p>Could not load claim information, please try again later.</p>;
        } else if(!claim) {
            return <p>Claim not found.</p>
        }
        const relationship = claim.relationship;
        return <div className="flex-gap">
            <PdsGrid variant="3up">
                <PdsGridItem>
                    <PdsHeading headingTag="h2" variant="title-sm">{claim.type} claim {claim.claimId}</PdsHeading>
                </PdsGridItem>
                <PdsGridItem>
                </PdsGridItem>
                <PdsGridItem>
                    <ClaimStatusButton claim={claim} style={{ pointerEvents: 'none' }}/>
                </PdsGridItem>
            </PdsGrid>
            <PdsGrid variant="3up">
                <PdsGridItem>
                    {claim.person && <>
                        <strong>
                            Person:{Relationship.YOURSELF === relationship
                            ? ''
                            : ` ${snakeCaseToCapitalized(findKeyByValue(Relationship, relationship))}`}
                        </strong>
                        <div>{claim.person}</div>
                    </>}
                </PdsGridItem>
                <PdsGridItem>
                    <strong>Submitted:</strong>
                    <div>{submissionDate}</div>
                </PdsGridItem>
                <PdsGridItem>
                    <strong>Total paid:</strong>
                    <div>{claim.amount.format()}</div>
                    <small>Represents all payments made on claim to date and should not be considered final unless status is "Approved".</small>
                </PdsGridItem>
            </PdsGrid>
        </div>
    }

    function buildRecentUpdates() {
        return <>
            {buildClaimStatus()}
            {buildDocuments()}
        </>;
    }

    function buildClaimStatus() {
        if(claimsLoading) {
            return <LoadingSpinner/>
        } else if(claimsError) {
            console.log('claims error', claimsError)
            return <p>Could not load claim information, please try again later.</p>;
        } else if(!claim) {
            return <p>Claim not found.</p>
        }
        const claimStatus = claim.status;
        const requestedInformation = claim.requestedInformation;
        return <PdsGrid variant="2up">
            <PdsGridItem>
                <div className="flex-gap">
                    <PdsHeading headingTag="h2" variant="title-sm">Recent updates</PdsHeading>
                    {claimStatus === ClaimStatus.DENIED &&
                        <div>
                            <div className="pds-u-flex-align-start">
                                <PdsIconCheckCircle size="default" color={pds['red']}/>
                                <strong>&nbsp;Claim denied</strong>
                            </div>
                            <div> Call {phoneNumber} for more information about the decision or to request to reopen this claim.</div>
                        </div>}
                    {claimStatus === ClaimStatus.APPROVED &&
                        <div>
                            <div className="pds-u-flex-align-start">
                                <PdsIconCheckCircle size="default" color={pds['green']}/>
                                <strong>&nbsp;Claim approved</strong>
                            </div>
                            <div>Your claim has been approved. You can view information about your claim payments below.</div>
                        </div>}
                    {claimStatus === ClaimStatus.NEEDS_MORE_INFORMATION && <>
                        <div>
                            <div className="pds-u-flex-align-start">
                                <PdsIconAlertTriangle size="default" color={pds['orange']}/>
                                <strong>&nbsp;Additional information requested</strong>
                            </div>
                            <div className="pds-u-padding-bottom-8">We need more information from the following parties to review your claim:</div>
                            {
                                [...requestedInformation].map(([party, requestedInformationByParty]) =>
                                    <>
                                        <div>From your {party}:</div>
                                        <ul>
                                            {requestedInformationByParty.map(information => <li>{information}</li>)}
                                        </ul>
                                    </>)
                            }
                            {requestedInformation.get(Party.EMPLOYER)?.length &&
                                <div>
                                    If you filed online, share the link you received in your email with your employer to submit their claim details.
                                    Otherwise, send them this paper form to file their portion of your claim: {buildPaperFormLink(coverage)}
                                </div>
                            }
                        </div>
                    </>}
                    <div>
                        <div>
                            <strong>Claim received {submissionDate}</strong>
                        </div>
                        <div>We will review your claim and collect any additional information. We'll contact you as soon as the review is
                            complete.
                        </div>
                    </div>
                </div>
            </PdsGridItem>
            <PdsGridItem>
                <div className="pds-u-background-brand-gradient-strong pds-u-typography-color-inverted-default pds-u-padding-20 flex-gap"
                     style={{ borderRadius: '10px' }}>
                    <PdsHeading headingTag="h3" variant="label-default">Claim analyst</PdsHeading>
                    {buildClaimAnalyst()}
                </div>
            </PdsGridItem>
        </PdsGrid>;
    }

    function buildPaperFormLink(coverage: Coverage) {
        if(memberLoading) {
            return <LoadingSpinner/>
        } else if(memberError) {
            console.log('member error', memberError)
            return <p>Could not load claim form link, please try again later.</p>;
        }
        return <AdobeDocuSignLink documentId={findPaperClaimFormNumberFor(coverage, member)}>Paper claim form</AdobeDocuSignLink>;
    }

    function buildDocuments() {
        if(notInClaimsService) {
            return;
        }
        const disableUpload = !claim || uploadInProgress;
        return <>
            <hr/>
            <PdsGrid variant="2up">
                <PdsGridItem>
                    <div className="flex-gap">
                        <PdsHeading headingTag="h2" variant="title-sm">Documents</PdsHeading>
                        <div>
                            Thank you for providing the following documents. Click on the name of the document to view it. New or
                            updated files can be uploaded.
                        </div>
                        {buildDocumentLinks()}
                    </div>
                </PdsGridItem>
                <PdsGridItem>
                    <div>
                        <PdsFileUpload id="document-upload"
                                       name="documentUpload"
                                       helpText="Files can be PDF, PNG, SVG, IMAGE, DOC, or DOCX no larger than 50mb"
                                       accept='.pdf,.png,.svg,.image,.doc,.docx'
                                       size={51200}
                                       ref={fileUploadRef}
                                       pdsFileUploadChange={async (event: Event) => {
                                           const file = (event as CustomEvent).detail?.summary?.[0];
                                           if (!file) {
                                               console.error('File could not be read. Please try again.');
                                               return;
                                           }
                                           setFile(file)
                                       }}
                                       pdsFileUploadRemove={() => {
                                           setFile(null as any as File);
                                       }}
                                       disabled={disableUpload}/>
                        <PdsSidebar>
                            <div>
                                <PdsTextInput
                                    id="file-description"
                                    name="fileDescription"
                                    label="File description"
                                    value={fileDescription}
                                    onChange={(event) => {
                                        setFileDescription(event.target.value)
                                    }}
                                />
                            </div>
                            <div slot="right-sidebar" className='pds-u-flex-align-end'>
                                <PdsButton type="button"
                                           disabled={disableUpload}
                                           onClick={async (e) => {
                                               e.preventDefault();
                                               await uploadDocument()
                                           }}>
                                    {uploadInProgress
                                        ? <div><PdsIconLoader className="pds-u-inline-block"/> Uploading</div>
                                        : <>Upload</>}
                                </PdsButton>
                            </div>
                        </PdsSidebar>
                    </div>
                    {uploadData && !uploadInProgress && !uploadError && <div>Document has been uploaded.</div>}
                </PdsGridItem>
            </PdsGrid>
        </>
    }

    function buildDocumentLinks() {
        if (claimsLoading) {
            return <LoadingSpinner/>
        } else if (claimsError) {
            console.log('claims error', claimsError)
            return <p>Could not load claim information, please try again later.</p>;
        } else if (!claim) {
            return <p>Claim not found.</p>
        }
        return claim.documents
            .sort((d1: Document, d2: Document) => d2.creationDate.toMillis() - d1.creationDate.toMillis())
            .map((d: Document) =>
                <div>
                    <PdsButton link="default"
                               type="button"
                               removeLinkPadding={true}
                               onClick={async () => {
                                   const claimDocumentResponse = await findClaimDocument({
                                       variables: {
                                           claimId,
                                           sharepointItemId: d.sharepointItemId
                                       }
                                   });
                                   const claimDocument = claimDocumentResponse.data.findClaimDocument;
                                   fileDownload(Buffer.from(claimDocument.content, 'base64'), claimDocument.name)
                               }}>
                        {`${d.name} (${d.creationDate.toLocaleString(DateTime.DATE_SHORT)})`}
                    </PdsButton>
                </div>)
    }

    function buildClaimAnalyst() {
        const assistance = `For assistance with your claim, please call ${phoneNumber} or e-mail SBDClaims@principal.com.`;
        if(notInClaimsService) {
            return assistance;
        } else if(analystLoading) {
            return <LoadingSpinner/>
        } else if(analystError) {
            console.log('analyst error', analystError)
            return 'Could not load claim analyst information, please try again later.';
        }
        if(!analyst) {
            return `This claim has not yet been assigned to an analyst. ${assistance}`
        }
        return <div>
            <div>{analyst.firstName}</div>
            <div>800-245-1522 x{analyst.phoneNumber?.replace(/\D/gi, '').slice(-5)} (Español: 800-243-1404)</div>
            <div>{analyst.emailAddress}</div>
        </div>;
    }

    function buildPayments() {
        return <>
            <div className="flex-gap">
                <PdsHeading headingTag="h2" variant="title-sm">Payment summary</PdsHeading>
                {buildUpdatePaymentPreference()}
                {buildPaymentTable()}
            </div>
            <PaymentPreferenceModal ref={paymentPreferenceRef}/>
        </>
    }

    function buildUpdatePaymentPreference() {
        if(notInClaimsService) {
            return;
        } else if(hasUpdatePaymentPreferenceLoading) {
            return <LoadingSpinner/>
        } else if(!hasUpdatePaymentPreference) {
            return;
        }
        return <PdsButton onClick={() => {
            paymentPreferenceRef.current.openModal()
        }}>
            Update payment preference
        </PdsButton>
    }

    function buildPaymentTable() {
        if(claimsLoading) {
            return <LoadingSpinner/>
        } else if(claimsError) {
            console.log('claims error', claimsError)
            return <p>Could not load claim information, please try again later.</p>;
        } else if(!claim) {
            return <p>Claim not found.</p>
        }
        const headers = ['Date paid', 'Payment amount', 'View EOB', 'Benefits']
        const tableProps = {
            title: "Payments",
            headers,
            rows: claim.payments
                .sort((p1: Payment, p2: Payment) => p2.effectiveDate.toMillis() - p1.effectiveDate.toMillis())
                .map((p: Payment) => buildPaymentRow(p))
        }
        return claim.payments?.length ? <DataTable {...tableProps} /> : 'No payments found.'
    }

    function buildPaymentRow(payment: Payment) {
        const paymentBenefits = payment?.benefits;
        const hasStartDate = paymentBenefits?.some(b => b?.startDate);
        const hasEndDate = paymentBenefits?.some(b => b?.endDate);
        return [payment.effectiveDate.toLocaleString(DateTime.DATE_SHORT),
            payment.amount.format(),
            <PdsButton type="button" onClick={async () => {
                const explanationOfBenefits = await findExplanationOfBenefits(
                    { variables: { claimId, coverage: findKeyByValue(Coverage, coverage), paymentId: payment.paymentId } });
                const explanationOfBenefitsContent = explanationOfBenefits.data.findExplanationOfBenefits.content
                fileDownload(Buffer.from(explanationOfBenefitsContent, 'base64'), 'ExplanationOfBenefits.pdf')
            }} disabled={!payment.explanationOfBenefitsDocumentInstanceId}>Download</PdsButton>,
            <PdsButton type="button" onClick={() => {
                setPaymentModalContents(
                    <FlatTable headers={[{ header: 'Benefit' }, { header: 'Amount' },
                                                    ...(hasStartDate ? [{ header: 'Start date' }] : []),
                                                    ...(hasEndDate ? [{ header: 'End date' }] : [])]}
                               rows={paymentBenefits.map(b => {
                                   return [b.benefit, `$${b.amount}`,
                                       ...(hasStartDate ? [b.startDate.toLocaleString(DateTime.DATE_SHORT)] : []),
                                       ...(hasEndDate ? [b.endDate.toLocaleString(DateTime.DATE_SHORT)] : [])];
                               })}/>);
                paymentModalRef.current.openModal()
            }} disabled={!paymentBenefits?.length}>
                View benefits
            </PdsButton>
        ]
    }

    async function uploadDocument() {
        if (!file) {
            return;
        }
        try {
            setUploadInProgress(true);
            const fileName = file.name;
            const fileSize = file.size;
            const fileExtension = findExtensionFor(fileName);
            const uploadDocumentInput = {
                claimId,
                name: fileDescription ? `${fileDescription}.${fileExtension}` : fileName,
                type: 'CLAIMS'
            };
            const response = (
                await createSharepointUploadSession({
                    variables: {
                        fileInput: {
                            fileSize: fileSize,
                            fileExtension: fileExtension
                        },
                        uploadDocumentInput: uploadDocumentInput
                    }
                })
            ).data.createSharepointUploadSession;

            const MICROSOFT_RECOMMENDED_SHAREPOINT_CHUNK_SIZE_BYTES = 60 * 1024 * 1024;
            for (let startByte = 0; startByte < fileSize; startByte += MICROSOFT_RECOMMENDED_SHAREPOINT_CHUNK_SIZE_BYTES) {
                const endByteExclusive = Math.min(startByte + MICROSOFT_RECOMMENDED_SHAREPOINT_CHUNK_SIZE_BYTES, fileSize);
                await uploadSharepointChunkWithRetry(response.uploadUrl, file, startByte, endByteExclusive, fileSize);
            }

            await completeSharepointUploadMutation({
                variables: {
                    uploadToken: response.uploadToken,
                    input: uploadDocumentInput
                },
                update: (cache, {data: {completeSharepointUpload}}) => {
                    cache.updateQuery({query: FIND_CLAIMS_QUERY, variables}, (data) => ({
                        findClaims: data.findClaims.map((c: DisabilityClaim) => {
                            if (c.claimId === claimId) {
                                return {
                                    ...c,
                                    documents: [...c.documents, completeSharepointUpload]
                                }
                            }
                            return c
                        })
                    }));
                }
            });
            fileUploadRef.current.uploadedFilesArray = [];
            setFileDescription("")
            setFile(null as any as File)
        } catch (e) {
            console.log('upload error', e)
        } finally {
            setUploadInProgress(false);
        }
    }

    function findExtensionFor(filename: string): string | undefined {
        return filename.split('.').pop();
    }

    async function uploadSharepointChunkWithRetry(
        uploadUrl: string,
        file: File,
        startByte: number,
        endByteExclusive: number,
        fileSize: number
    ): Promise<void> {
        const chunk = file.slice(startByte, endByteExclusive);
        const contentRange = `bytes ${startByte}-${endByteExclusive - 1}/${fileSize}`;
        const RETRYABLE_UPLOAD_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);

        const axiosInstance = axios.create();
        axiosRetry(axiosInstance, {
            retries: 3,
            retryDelay: axiosRetry.exponentialDelay,
            retryCondition: (error) => RETRYABLE_UPLOAD_STATUS_CODES.has(error.status as number),
            shouldResetTimeout: true
        })
        await axiosInstance.put(uploadUrl, chunk, {
            headers: {
                'Content-Range': contentRange
            }
        });
    }
};

export default ClaimDetail;
